import { useQuery } from "@apollo/client";
import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import { useSnackbar } from "notistack";
import { FC, useMemo } from "react";
import { Link } from "react-router-dom";
import * as yup from "yup";

import { makeStyles, MenuItem } from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import ErrorCard from "src/components/ErrorCard";
import LoadingIndicator from "src/components/LoadingIndicator";
import { useSubaccount } from "src/components/SubaccountProvider";
import { FormikSelect } from "src/components/form/Select";
import {
  campaignEditVanIntegration,
  campaignEditVanIntegrationVariables
} from "src/graphql/generated/campaignEditVanIntegration";
import { getCampaign_campaign } from "src/graphql/generated/getCampaign";
import {
  CampaignStatus,
  VanDatabaseMode
} from "src/graphql/generated/globalTypes";
import {
  listVanIntegrations,
  listVanIntegrationsVariables,
  listVanIntegrations_listVanIntegrations
} from "src/graphql/generated/listVanIntegrations";
import {
  CAMPAIGN_EDIT_VAN_INTEGRATION,
  LIST_VAN_INTEGRATIONS
} from "src/graphql/integrations";
import useMutation from "src/util/useMutation";
import { vanModeFriendlyName } from "src/util/van";

import { useCampaignEditEvents } from "./CampaignEditorsProvider";
import { hasContacts } from "./Contacts";
import ContinueButton from "./Contacts/ContinueButton";
import { FormikSaveButtons } from "./SaveButtons";

type FormValues = {
  vanIntegrationId: string;
  vanDatabaseMode: VanDatabaseMode | "";
  goToNext?: boolean;
};

export default function Integration({
  campaign,
  goToNextSection
}: {
  campaign: getCampaign_campaign;
  goToNextSection: () => void;
}): JSX.Element {
  return (
    <>
      <h2>Integration</h2>
      <CampaignIntegrations
        campaign={campaign}
        goToNextSection={goToNextSection}
      />
    </>
  );
}

const CampaignIntegrations: FC<{
  campaign: getCampaign_campaign;
  goToNextSection: () => void;
}> = ({ campaign, goToNextSection }) => {
  const { subaccount, subaccountLink } = useSubaccount()!;
  const { enqueueSnackbar } = useSnackbar();

  const { data, error, loading } = useQuery<
    listVanIntegrations,
    listVanIntegrationsVariables
  >(LIST_VAN_INTEGRATIONS, {
    variables: {
      includeDeleted: true,
      subaccountId: subaccount.id
    }
  });
  const [editCampaignIntegration] = useMutation<
    campaignEditVanIntegration,
    campaignEditVanIntegrationVariables
  >(CAMPAIGN_EDIT_VAN_INTEGRATION, {
    onErrorSnackbarMessage: "Error saving campaign",
    refetchQueries: ["getCampaign"],
    awaitRefetchQueries: true
  });

  const sendEditEvent = useCampaignEditEvents();

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  // We only want to present non-deleted integrations, as well as the current
  // integration if it is deleted
  const vanIntegrations = (data?.listVanIntegrations || [])
    .filter(integration =>
      integration.deletedAt
        ? integration.id === campaign.vanIntegration?.vanIntegration.id
        : true
    )
    .map(integration =>
      integration.deletedAt
        ? { ...integration, name: `${integration.name} (archived)` }
        : integration
    );

  if (!vanIntegrations?.length) {
    return (
      <>
        <p>You don't have any integrations yet!</p>
        <p>
          Go to <Link to={subaccountLink("/integrations")}>integrations</Link>{" "}
          to add one.
        </p>
        <ContinueButton handleClick={goToNextSection} />
      </>
    );
  }

  const handleSave = async (
    { vanIntegrationId, vanDatabaseMode, goToNext }: FormValues,
    { setSubmitting, resetForm }: FormikHelpers<FormValues>
  ) => {
    const { errors, data } = await editCampaignIntegration({
      variables: {
        id: campaign.id,
        integration: vanIntegrationId
          ? {
              vanIntegrationId,
              databaseMode: vanDatabaseMode as VanDatabaseMode
            }
          : null
      }
    });

    setSubmitting(false);
    if (!errors) {
      sendEditEvent();
      const integration = data!.campaignEditVanIntegration.vanIntegration;
      resetForm({
        values: {
          vanIntegrationId: integration?.vanIntegration?.id ?? "",
          vanDatabaseMode: integration?.databaseMode ?? ""
        }
      });
      if (goToNext) {
        goToNextSection();
      } else {
        enqueueSnackbar("Saved campaign", { variant: "info" });
      }
    }
  };

  return (
    <Formik
      initialValues={{
        vanIntegrationId: campaign.vanIntegration?.vanIntegration.id ?? "",
        vanDatabaseMode: campaign.vanIntegration?.databaseMode ?? ""
      }}
      onSubmit={handleSave}
      validationSchema={yup.object().shape({
        vanIntegrationId: yup.string(),
        vanDatabaseMode: yup
          .mixed<VanDatabaseMode | "">()
          .oneOf([...Object.values(VanDatabaseMode), ""])
          .when(
            "vanIntegrationId",
            (vanIntegrationId: string | null, schema: yup.AnySchema) =>
              vanIntegrationId?.length
                ? schema.required("Required")
                : schema.notRequired()
          )
      })}
    >
      <CampaignIntegrationForm
        campaign={campaign}
        vanIntegrations={vanIntegrations}
        goToNextSection={goToNextSection}
      />
    </Formik>
  );
};

const useStyles = makeStyles(theme => ({
  warning: {
    marginBottom: theme.spacing(3)
  }
}));

const CampaignIntegrationForm: FC<{
  campaign: getCampaign_campaign;
  vanIntegrations: listVanIntegrations_listVanIntegrations[];
  goToNextSection: () => void;
}> = ({ campaign, vanIntegrations, goToNextSection }) => {
  const classes = useStyles();

  const { values, setFieldValue } = useFormikContext<FormValues>();

  const databaseModes = useMemo(() => {
    const vanIntegrationId: string = values.vanIntegrationId;
    if (!vanIntegrationId?.length) {
      return null;
    }
    const vanIntegration = vanIntegrations.find(
      i => i.id === vanIntegrationId
    )!;

    const availableDatabaseModes: VanDatabaseMode[] = [];
    if (vanIntegration.hasMyCampaign) {
      availableDatabaseModes.push(VanDatabaseMode.MY_CAMPAIGN);
    }
    if (vanIntegration.hasMyVoters) {
      availableDatabaseModes.push(VanDatabaseMode.MY_VOTERS);
    }

    if (availableDatabaseModes.length === 1) {
      setFieldValue("vanDatabaseMode", availableDatabaseModes[0]);
    }

    return availableDatabaseModes;
  }, [values, setFieldValue, vanIntegrations]);

  const campaignHasContacts = hasContacts(campaign);

  const disableChanges = campaign.status !== CampaignStatus.DRAFT;

  return (
    <Form>
      {campaignHasContacts ? (
        <Alert severity="warning" className={classes.warning}>
          {disableChanges
            ? "Integration cannot be changed after campaign has started."
            : "Previously imported contacts will be deleted if integration is changed."}
        </Alert>
      ) : null}
      <FormikSelect
        name="vanIntegrationId"
        withNoneOption
        fullWidth
        label="Pick an integration"
        disabled={disableChanges}
      >
        {vanIntegrations.map(integration => (
          <MenuItem key={integration.id} value={integration.id}>
            {integration.name}
          </MenuItem>
        ))}
      </FormikSelect>
      {databaseModes ? (
        <FormikSelect
          fullWidth
          name="vanDatabaseMode"
          disabled={databaseModes.length === 1 || disableChanges}
        >
          {databaseModes.map(databaseMode => (
            <MenuItem value={databaseMode} key={databaseMode}>
              {vanModeFriendlyName(databaseMode)}
            </MenuItem>
          ))}
        </FormikSelect>
      ) : null}
      <FormikSaveButtons
        continueWithoutSaving={goToNextSection}
        confirmSubmit={
          campaignHasContacts
            ? {
                title: "Save Integration",
                description:
                  "Are you sure? This will remove all contacts from this campaign."
              }
            : null
        }
        disabled={disableChanges}
      />
    </Form>
  );
};
